import React, { useEffect, useCallback } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import MediaViewerControls from "./MediaViewerControls";
import { useMediaViewer } from "./useMediaViewer";
// ImageViewer uses pure Tailwind CSS, no additional styles needed

interface ImageViewerProps {
  url: string;
  alt?: string;
  /** Override the default non-fullscreen height (e.g. "h-[50vh]") */
  containerClassName?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  url,
  alt = "Image",
  containerClassName,
}) => {
  const {
    rotation,
    isFullscreen,
    isCustomFullscreen,
    isAnyFullscreen,
    isLoading,
    error,
    containerRef,
    handleRotate,
    toggleFullscreen,
    handleError,
    handleLoadStart,
    handleLoadSuccess,
    getMediaWidth,
    createFullscreenPortal,
  } = useMediaViewer({
    onError: (error) => console.error("Image viewer error:", error),
  });

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.key) {
        case "r":
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleRotate();
          }
          break;
        case "f":
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            toggleFullscreen();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [handleRotate, toggleFullscreen]);

  const handleImageLoad = useCallback(() => {
    handleLoadSuccess();
  }, [handleLoadSuccess]);

  const handleImageError = useCallback(() => {
    handleError("Failed to load image");
  }, [handleError]);

  // Start loading when component mounts
  useEffect(() => {
    handleLoadStart();
  }, [url, handleLoadStart]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-96 bg-gray-50 rounded-lg">
        <div className="text-red-500 text-center">
          <p className="text-lg font-medium mb-2">Error Loading Image</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const viewerContent = (
    <div
      ref={containerRef}
      className={`relative bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden touch-none select-none ${
        isAnyFullscreen
          ? "w-full h-full bg-black"
          : `w-full ${containerClassName ?? "h-[70vh] md:h-[80vh]"}`
      }`}
    >
      <TransformWrapper
        initialScale={1}
        minScale={0.3}
        maxScale={5}
        centerOnInit={true}
        wheel={{
          step: 0.1,
          activationKeys: [], // Disable ctrl+wheel requirement for mobile
        }}
        pinch={{
          step: 5,
        }}
        doubleClick={{
          disabled: false,
          step: 2,
        }}
        panning={{
          disabled: false,
          velocityDisabled: false,
        }}
        limitToBounds={false}
        centerZoomedOut={true}
        alignmentAnimation={{
          sizeX: 0,
          sizeY: 0,
          velocityAlignmentTime: 0.2,
        }}
        velocityAnimation={{
          sensitivity: 1,
          animationTime: 0.2,
        }}
      >
        <MediaViewerControls
          isFullscreen={isFullscreen}
          isCustomFullscreen={isCustomFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onRotate={handleRotate}
          showPageControls={false}
        />

        <TransformComponent
          wrapperStyle={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
          contentStyle={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="flex items-center justify-center w-full h-full p-4">
            {isLoading && (
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  Loading image...
                </p>
              </div>
            )}

            <img
              src={url}
              alt={alt}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={`max-w-full max-h-full object-contain shadow-lg bg-white rounded transition-opacity duration-200 ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
              style={{
                transform: `rotate(${rotation}deg)`,
                maxWidth: `${getMediaWidth()}px`,
              }}
            />
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );

  return createFullscreenPortal(viewerContent);
};

export default ImageViewer;
